import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logWarn } from '@/lib/log';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const HOME_BY_ROLE = { admin: '/admin', driver: '/driver', pump: '/pump' };
const routeFor = (u) => HOME_BY_ROLE[u.role] || '/app';

export default function AuthCallback() {
  const { loginWithData } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false); // useRef (not useState): survives StrictMode double-invoke
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) { nav('/signin', { replace: true }); return; }

    (async () => {
      try {
        const r = await api.post('/auth/oauth/session', { session_id: sessionId });
        const u = loginWithData(r.data);
        // Discard the one-time session_id from the URL before moving on.
        window.history.replaceState(null, '', window.location.pathname);
        toast.success(r.data.created ? `Welcome, ${u.name}! Your account is ready.` : `Welcome back, ${u.name}`);
        nav(routeFor(u), { replace: true, state: { user: u } });
      } catch (e) {
        logWarn('Google sign-in exchange failed', e);
        window.history.replaceState(null, '', window.location.pathname);
        setError(e.response?.data?.detail || 'Google sign-in failed. Please try again.');
        setTimeout(() => nav('/signin', { replace: true }), 2500);
      }
    })();
  }, [location.hash, loginWithData, nav]);

  return (
    <div className="mfk-theme min-h-screen bg-background text-foreground flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        {error ? (
          <p className="text-sm text-destructive" data-testid="auth-callback-error">{error}</p>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="mt-3 text-sm text-muted-foreground">Completing Google sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
